/**
 * Routine-profile vaccination API helpers.
 * Used only by Linkedchildren / Vaccinationuwin — NOT the session drawer flow.
 * Profile APIs expect encrypted beneficiary_id; session flow uses a different contract.
 */

import { uwinAPI } from '../../../../services/uwin.api';
import { buildPreviousMaterialDetails } from '../memberVaccination/materialDetailsService';

const encryptText = async (plaintext) => {
  const res = await uwinAPI.encryptText({ plaintext: String(plaintext) });
  return res.data.encrypted;
};

/**
 * Submit a previous vaccination record from the routine (profile) flow.
 * @param {Object} formData - Formik values; must include previous_vaccines
 * @param {Object} beneficiaryData - Plain beneficiary row (beneficiary_id is encrypted here)
 */
export const createRoutinePreviousVaccinationRecord = async (
  formData,
  beneficiaryData
) => {
  if (!beneficiaryData?.beneficiary_id) {
    throw new Error('beneficiary_id is required');
  }

  const encryptedBeneficiaryId = await encryptText(beneficiaryData.beneficiary_id);
  const materialDetails = await buildPreviousMaterialDetails(formData, encryptText);

  const payload = {
    vaccination_status: true,
    is_past_dated: true,
    beneficiary_id: encryptedBeneficiaryId,
    materialDetails,
  };

  const response = await uwinAPI.createVaccinationRecord(payload);

  if (response?.success) {
    return {
      success: true,
      data: response.data,
      message: response.message || 'Previous vaccination recorded successfully',
      payload,
    };
  }

  throw new Error(response?.message || 'Failed to save vaccination record');
};

export { encryptText as routineEncryptText };

/**
 * Map vaccinations-chart rows into beneficiary-details vaccination[] shape
 * so ChildPreviousRules / PWPreviousRules can mark doses as administered.
 */
export const mapChartDetailsToAdministeredRecords = async (
  vaccinationDetails = [],
  allUipMaterialsRaw = [],
  encryptFn = encryptText
) => {
  const records = [];

  for (const row of vaccinationDetails) {
    if (!row?.vaccination_date || !String(row.vaccination_date).trim()) {
      continue;
    }

    if (row.material_name && row.dose) {
      records.push({
        material_name: row.material_name,
        name: row.material_name,
        material_id: row.material_id,
        dose: row.dose,
        vaccination_date: row.vaccination_date,
      });
      continue;
    }

    let materialName = row.material_name || row.name || row.vaccine_name;
    let doseNumber = null;

    if (row.display_dose_name && allUipMaterialsRaw?.length) {
      const matchedMaterial = allUipMaterialsRaw.find((material) =>
        (material.display_dose_names || []).includes(row.display_dose_name)
      );

      if (matchedMaterial) {
        materialName = matchedMaterial.material_name;
        const doseIndex = matchedMaterial.display_dose_names.indexOf(
          row.display_dose_name
        );
        if (doseIndex >= 0) {
          doseNumber = doseIndex + 1;
        }
      }
    }

    if (!materialName) {
      continue;
    }

    let dose = row.dose || row.material_dose;
    if (!dose && doseNumber) {
      dose = await encryptFn(doseNumber);
    }

    if (!dose) {
      continue;
    }

    records.push({
      material_name: materialName,
      name: materialName,
      material_id: row.material_id,
      dose,
      vaccination_date: row.vaccination_date,
    });
  }

  return records;
};

/**
 * Combine profile vaccination history with vaccinations-chart rows.
 * Profile `vaccination[]` is often empty in routine flow; chart has administered doses.
 */
export const mergeAdministeredVaccinationHistory = (
  profileVaccination = [],
  chartRecords = []
) => {
  const profile = profileVaccination || [];
  const chart = chartRecords || [];
  if (profile.length === 0) return chart;
  if (chart.length === 0) return profile;
  return [...profile, ...chart];
};
